<template>
  <div ref="chartContainer" class="bg-white border border-gray-200 rounded-lg">
    <div class="px-4 py-3 bg-secondary-100 border-b border-secondary-200 rounded-t-lg">
      <h3 class="font-medium text-secondary-900 text-sm">Peer Comparison</h3>
    </div>
    <div v-if="loading" class="flex items-center justify-center h-[400px]">
      <div class="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
    </div>
    <div v-else-if="chartData.series.length > 0" :style="{ height: chartHeight + 'px' }">
      <Chart_V5
        :key="chartKey"
        :chart_data="chartData"
        :chart_config="chartConfig"
        override-height="h-full"
      />
    </div>
    <div v-else class="flex items-center justify-center h-[200px] text-gray-400 text-sm">
      No peer comparison data available
    </div>

    <!-- Controls -->
    <div v-if="sortedData.length > 0" class="border-t border-gray-200">
      <div class="px-3 py-1.5 flex flex-wrap items-center justify-center gap-3 text-xs text-gray-600">
        <label v-if="parentOptions.length > 0" class="flex items-center gap-1">
          <span class="font-medium">Parent</span>
          <select v-model="selectedParentId" @change="handleParentChange" class="border border-gray-200 rounded px-1.5 py-0.5 text-xs">
            <option v-for="p in parentOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
        </label>

        <label class="flex items-center gap-1">
          <span class="font-medium">View</span>
          <select v-model="paginationMode" @change="resetPage" class="border border-gray-200 rounded px-1.5 py-0.5 text-xs">
            <option value="sample">Sample</option>
            <option value="ranked">Ranked</option>
          </select>
        </label>

        <label class="flex items-center gap-1">
          <span class="font-medium">Per page</span>
          <select v-model.number="rowsPerPage" @change="resetPage" class="border border-gray-200 rounded px-1.5 py-0.5 text-xs">
            <option v-for="n in [5, 10, 15, 20, 25, 30, 40]" :key="n" :value="n">{{ n }}</option>
          </select>
        </label>

        <div v-if="totalPages > 1" class="flex items-center gap-1">
          <button @click="page = Math.max(0, page - 1)" :disabled="page === 0"
            class="px-1.5 py-0.5 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-40">&lt;</button>
          <span>{{ page + 1 }}/{{ totalPages }}</span>
          <button @click="page = Math.min(totalPages - 1, page + 1)" :disabled="page >= totalPages - 1"
            class="px-1.5 py-0.5 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-40">&gt;</button>
        </div>

        <label v-if="isPercentage" class="flex items-center gap-1 cursor-pointer">
          <input type="checkbox" v-model="zoomToData" class="rounded border-gray-300 w-3 h-3" @change="chartKey++" />
          <span>Zoom</span>
        </label>

        <label class="flex items-center gap-1 cursor-pointer">
          <input type="checkbox" v-model="hideOtherNames" class="rounded border-gray-300 w-3 h-3" />
          <span>Hide names</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import { toProperCase } from '@/utils/common';

export default {
  name: 'PerformanceCohort',
  components: { Chart_V5 },
  props: {
    metricIds: { type: Array, default: () => [] },
    year: { type: Number, required: true },
    entityId: { type: Number, default: null },
    maxRows: { type: Number, default: 20 },
  },
  data() {
    return {
      loading: false,
      cohortData: null,
      metricMeta: null,
      entityParents: [],
      selectedParentId: null,
      currentEntityType: null,
      paginationMode: 'sample',
      zoomToData: true,
      hideOtherNames: false,
      page: 0,
      rowsPerPage: null,
      chartKey: 0,
      resizeObserver: null,
      resizeDebounceTimer: null,
    };
  },
  computed: {
    metricId() {
      return this.metricIds.length ? this.metricIds[0] : null;
    },
    metricName() {
      return this.metricMeta?.name || 'Metric';
    },
    displayConfig() {
      return this.metricMeta?.display_config || {};
    },
    isPercentage() {
      return this.displayConfig.format === 'percentage' ||
        this.metricMeta?.composable_config?.aggregation_type === 'percentage';
    },
    isCurrency() {
      return this.displayConfig.format === 'currency';
    },
    isLowerBetter() {
      return this.displayConfig.higher_is_better === false;
    },
    decimalPlaces() {
      return this.displayConfig.decimal_places ?? 1;
    },
    viewOptions() {
      return [
        { value: 'sample', label: 'Sample' },
        { value: 'ranked', label: 'Ranked' },
      ];
    },
    parentOptions() {
      return this.entityParents.map(p => ({
        value: p.id,
        label: `${p.entity_type_name} - ${p.name}`,
      }));
    },
    totalPages() {
      if (!this.sortedData.length) return 1;
      return Math.ceil(this.sortedData.length / this.rowsPerPage);
    },
    isDecimal() {
      return this.displayConfig.format === 'decimal' || this.displayConfig.format === 'number';
    },
    getValueFn() {
      const isRate = this.isPercentage || this.isCurrency || this.isDecimal;
      return isRate
        ? (r) => r.denominator > 0 ? r.numerator / r.denominator : 0
        : (r) => r.numerator || 0;
    },
    sortedData() {
      if (!this.cohortData?.results?.length) return [];

      const getValue = this.getValueFn;

      // Filter to current year rows only, deduplicate by entity
      const byEntity = {};
      for (const r of this.cohortData.results) {
        const yr = r.observation_dt_year ?? parseInt(String(r.observation_date).substring(0, 4), 10);
        if (yr !== this.year) continue;
        const key = r.entity_id || r.entity_name;
        if (!byEntity[key] || r.observation_date > byEntity[key].observation_date) {
          byEntity[key] = r;
        }
      }

      return Object.values(byEntity).sort((a, b) => getValue(b) - getValue(a));
    },
    paginatedData() {
      const data = this.sortedData;
      if (data.length <= this.rowsPerPage) return data;

      // Check if current entity would naturally appear on this page
      const currentNotInPage = this.entityId && !this.currentEntityOnPage(data);

      let pageData;
      if (currentNotInPage) {
        // Entity not on this page: reserve one slot, fill rest from peers, insert entity
        const slotCount = this.rowsPerPage - 1;
        const peers = data.filter(r => r.entity_id !== this.entityId);

        if (this.paginationMode === 'ranked') {
          const start = this.page * slotCount;
          pageData = peers.slice(start, start + slotCount);
        } else {
          const totalPages = Math.ceil(peers.length / slotCount);
          pageData = peers.filter((_, idx) => idx % totalPages === this.page);
        }

        const current = data.find(r => r.entity_id === this.entityId);
        if (current) {
          const getValue = this.getValueFn;
          const currentValue = getValue(current);
          let insertIdx = pageData.findIndex(r => currentValue > getValue(r));
          if (insertIdx === -1) insertIdx = pageData.length;
          pageData.splice(insertIdx, 0, current);
        }
      } else {
        // Entity naturally on this page (or no entity): straight slice
        if (this.paginationMode === 'ranked') {
          const start = this.page * this.rowsPerPage;
          pageData = data.slice(start, start + this.rowsPerPage);
        } else {
          pageData = data.filter((_, idx) => idx % this.totalPages === this.page);
        }
      }

      return pageData;
    },
    chartHeight() {
      const rows = this.paginatedData.length;
      return Math.max(200, rows * 25 + 120);
    },
    valueRange() {
      if (this.isCurrency || this.isDecimal) return { min: 0, max: null };
      if (!this.isPercentage || !this.zoomToData) return { min: 0, max: this.isPercentage ? 1 : null };

      const data = this.sortedData;
      if (!data.length) return { min: 0, max: 1 };

      const values = data.map(r => r.denominator > 0 ? r.numerator / r.denominator : 0);
      const dataMin = Math.min(...values);
      const dataMax = Math.max(...values);
      const range = dataMax - dataMin;
      const padding = Math.max(range * 0.15, 0.02);

      return {
        min: Math.max(0, Math.floor((dataMin - padding) * 20) / 20),
        max: Math.min(1, Math.ceil((dataMax + padding) * 20) / 20),
      };
    },
    chartData() {
      const data = this.paginatedData;
      if (!data.length) return { series: [], labels: [], colors: [], filter_exclusions: [] };

      const getValue = this.getValueFn;
      const isCurrent = (r) => this.entityId && r.entity_id === this.entityId;

      return {
        series: [{
          values: data.map(getValue),
          config: {
            label: 'Comparison',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            yAxis: 'left',
            offset: 0,
          },
        }],
        labels: data.map(r =>
          isCurrent(r) || !this.hideOtherNames
            ? toProperCase(r.entity_name || 'Unknown')
            : '[Other entity]'
        ),
        colors: data.map(r =>
          isCurrent(r) ? 'rgba(255, 150, 150, 0.2)' : 'rgba(75, 192, 192, 0.2)'
        ),
        filter_exclusions: data.map(r => isCurrent(r) ? 1 : 0),
      };
    },
    chartConfig() {
      return {
        title: this.metricName + (this.isLowerBetter ? ' (Lower is Better)' : ''),
        subtitle: this.isPercentage ? 'Comparison across Organizations' : this.isCurrency ? 'Cost Comparison' : this.isDecimal ? 'Value Comparison' : 'Count Comparison',
        chartType: 'horizontal_bar',
        margin: { top: 80, right: 75, bottom: 40, left: 225 },
        xAxis: { labelRotation: 0, tickFormat: null, fontSize: 12 },
        yAxes: [{ position: 'left', tickFormat: null, fontSize: 12, min: this.valueRange.min, max: this.valueRange.max }],
        legend: { display: true, position: 'top' },
        formatting: { percentage: this.isPercentage, currency: this.isCurrency, sig_figs: this.decimalPlaces },
        labelDisplayConfig: { minLabelSpacing: 5 },
      };
    },
  },
  watch: {
    metricIds() { this.fetchAll(); },
    year() { this.fetchCohortData(); },
    entityId() { this.fetchAll(); },
  },
  methods: {
    toProperCase,
    currentEntityOnPage(data) {
      if (!this.entityId) return true;
      // Check if entity would naturally appear on current page
      const filtered = data.filter(r => r.entity_id !== this.entityId);
      const idx = data.findIndex(r => r.entity_id === this.entityId);
      if (idx === -1) return true;
      if (this.paginationMode === 'ranked') {
        const start = this.page * this.rowsPerPage;
        return idx >= start && idx < start + this.rowsPerPage;
      }
      return idx % this.totalPages === this.page;
    },
    resetPage() {
      this.page = 0;
      this.chartKey += 1;
    },
    handleParentChange() {
      this.page = 0;
      this.fetchCohortData();
    },
    async fetchAll() {
      this.loading = true;
      await this.fetchEntityInfo();
      await this.fetchCohortData();
    },
    async fetchEntityInfo() {
      this.entityParents = [];
      this.selectedParentId = null;
      this.currentEntityType = null;
      if (!this.entityId) return;
      try {
        const [entityResponse, parentsResponse] = await Promise.all([
          axios.get(`api/entities/${this.entityId}/`),
          axios.get(`api/entities/${this.entityId}/parents/`),
        ]);
        this.currentEntityType = entityResponse.data.entity_type_name;
        this.entityParents = parentsResponse.data;
        if (this.entityParents.length) {
          this.selectedParentId = this.entityParents[0].id;
        }
      } catch (error) {
        console.error('Error fetching entity info:', error);
      }
    },
    async fetchCohortData() {
      if (!this.metricId) return;
      this.loading = true;
      try {
        const payload = {
          observation_year: this.year,
          group_by_contract: false,
        };

        if (this.selectedParentId) {
          payload.entity_id = this.selectedParentId;
        }
        if (!this.currentEntityType) return;
        payload.entity_type = this.currentEntityType;

        const [metaResponse, dataResponse] = await Promise.all([
          axios.get(`api/impact_analytics/composable-metrics/${this.metricId}/`),
          axios.post(`api/impact_analytics/composable-metrics/${this.metricId}/run/`, payload),
        ]);
        this.metricMeta = metaResponse.data;
        this.cohortData = dataResponse.data;
        this.page = 0;
        this.chartKey += 1;
      } catch (error) {
        console.error('Error fetching cohort data:', error);
      } finally {
        this.loading = false;
      }
    },
    initResizeObserver() {
      if (!this.$refs.chartContainer) return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
        this.resizeDebounceTimer = setTimeout(() => {
          if (!this.loading) this.chartKey += 1;
        }, 100);
      });
      this.resizeObserver.observe(this.$refs.chartContainer);
    },
  },
  mounted() {
    this.rowsPerPage = this.maxRows;
    this.fetchAll();
    this.initResizeObserver();
  },
  beforeUnmount() {
    if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
};
</script>
